<template>
  <div class="call-info-row" :class="{ 'is-loading': isUpdating }">
    <div class="call-info-main">
      <div v-if="isUpdating" class="call-info-loading-overlay" aria-hidden="true">
        <Icon icon="svg-spinners:ring-resize" width="20" height="20" class="loading-spinner" />
        <span class="loading-label">Saving…</span>
      </div>

      <div class="validity-dropdown" ref="validityDropdown">
        <div
          class="validity-selector"
          :class="{ 'is-open': isValidityOpen, 'is-disabled': isUpdating }"
          @click="!isUpdating && toggleValidityDropdown()"
        >
          <span class="validity-icon" :class="getValidityClass(internalValidity || 'Unverified')">
            <Icon v-if="internalValidity === 'Valid'" icon="mdi:check" width="16" height="16" />
            <Icon
              v-else-if="internalValidity === 'Invalid'"
              icon="mdi:close"
              width="16"
              height="16"
            />
            <Icon v-else icon="mdi:help" width="16" height="16" />
          </span>
          <Icon
            icon="mdi:chevron-down"
            width="14"
            height="14"
            class="validity-dropdown-icon"
            :class="{ rotated: isValidityOpen }"
          />
        </div>

        <Teleport to="body">
          <transition name="dropdown-fade">
            <div
              v-if="isValidityOpen"
              ref="validityDropdownMenu"
              class="validity-dropdown-menu"
              :style="validityDropdownStyle"
            >
              <div
                class="validity-option"
                :class="{ selected: internalValidity === 'Valid' }"
                @click="selectValidity('Valid')"
              >
                <span class="validity-icon valid">
                  <Icon icon="mdi:check" width="14" height="14" />
                </span>
                <span class="validity-label">Valid</span>
              </div>
              <div
                class="validity-option"
                :class="{ selected: internalValidity === 'Invalid' }"
                @click="selectValidity('Invalid')"
              >
                <span class="validity-icon invalid">
                  <Icon icon="mdi:close" width="14" height="14" />
                </span>
                <span class="validity-label">Invalid</span>
              </div>
              <div
                class="validity-option"
                :class="{ selected: internalValidity === 'Unverified' }"
                @click="selectValidity('Unverified')"
              >
                <span class="validity-icon unverified">
                  <Icon icon="mdi:help" width="14" height="14" />
                </span>
                <span class="validity-label">Unverified</span>
              </div>
            </div>
          </transition>
        </Teleport>
      </div>

      <div class="phone-number">
        {{ phoneNumber }}
      </div>

      <div class="call-type-dropdown" ref="callTypeDropdown" style="position: relative">
        <div
          class="call-type-selector"
          :class="{ 'is-open': isCallTypeOpen, 'is-disabled': isUpdating }"
          @click="!isUpdating && toggleCallTypeDropdown()"
        >
          <span class="call-type-label">{{ internalCallType }}</span>
          <Icon
            icon="mdi:chevron-down"
            width="12"
            height="12"
            class="dropdown-icon"
            :class="{ rotated: isCallTypeOpen }"
          />
        </div>

        <Teleport to="body">
          <transition name="dropdown-fade">
            <div
              v-if="isCallTypeOpen"
              ref="callTypeDropdownMenu"
              class="call-type-dropdown-menu"
              :style="callTypeDropdownStyle"
            >
              <div
                v-for="type in CALL_TYPES"
                :key="type"
                class="call-type-option"
                :class="{ selected: internalCallType === type }"
                @click="selectCallType(type)"
              >
                {{ type }}
              </div>
            </div>
          </transition>
        </Teleport>

        <div v-if="showIVRInput" class="detail-popover" @click.stop @mousedown.stop>
          <button class="detail-popover-close" @click="showIVRInput = false" type="button">
            <Icon icon="mdi:close" width="12" height="12" />
          </button>
          <label class="input-label">IVR Numbers</label>
          <div class="ivr-group">
            <input
              v-for="(part, idx) in internalIvrParts"
              :key="idx"
              ref="ivrInputRefs"
              v-model="internalIvrParts[idx]"
              type="text"
              class="input-field ivr-field"
              @keyup.enter="saveIVR"
              @focus="onIvrFocus"
            />
            <button
              class="icon-action save"
              type="button"
              :disabled="isUpdating"
              :aria-busy="isUpdating"
              @click="saveIVR"
              title="Save"
            >
              <Icon v-if="isUpdating" icon="svg-spinners:ring-resize" width="14" height="14" />
              <Icon v-else icon="mdi:check" width="14" height="14" />
            </button>
            <button
              class="icon-action delete"
              type="button"
              :disabled="isUpdating"
              @click="deleteIVR"
              title="Delete"
            >
              <Icon icon="mdi:delete-outline" width="14" height="14" />
            </button>
          </div>
        </div>

        <div v-if="showGateKeeperInput" class="detail-popover" @click.stop @mousedown.stop>
          <button class="detail-popover-close" @click="showGateKeeperInput = false" type="button">
            <Icon icon="mdi:close" width="12" height="12" />
          </button>
          <label class="input-label">Gate Keeper Name</label>
          <div class="gk-group">
            <input
              ref="gateKeeperInputRef"
              v-model="internalGateKeeperName"
              type="text"
              class="input-field gk-field"
              :disabled="isUpdating"
              @keyup.enter="saveGateKeeper"
              @focus="onGateKeeperFocus"
            />
            <button
              class="icon-action save"
              type="button"
              :disabled="isUpdating"
              :aria-busy="isUpdating"
              @click="saveGateKeeper"
              title="Save"
            >
              <Icon v-if="isUpdating" icon="svg-spinners:ring-resize" width="14" height="14" />
              <Icon v-else icon="mdi:check" width="14" height="14" />
            </button>
            <button
              class="icon-action delete"
              type="button"
              :disabled="isUpdating"
              @click="deleteGateKeeper"
              title="Delete"
            >
              <Icon icon="mdi:delete-outline" width="14" height="14" />
            </button>
          </div>
        </div>
      </div>

      <div class="action-icons">
        <button
          class="icon-btn phone-btn"
          :class="{ 'active-call-btn': isInCall && isCurrentCall }"
          type="button"
          :disabled="isUpdating"
          @click="handlePhoneClick"
        >
          <Icon
            :icon="isInCall && isCurrentCall ? 'mdi:phone-hangup' : 'mdi:phone'"
            width="16"
            height="16"
          />
        </button>
        <button
          class="icon-btn menu-btn"
          type="button"
          ref="actionsDropdown"
          :disabled="isUpdating"
          @click="onMenuButtonClick"
        >
          <Icon icon="mdi:dots-vertical" width="16" height="16" />
        </button>
      </div>
    </div>

    <transition name="slide-fade">
      <div
        v-if="internalCallType === 'IVR' || internalCallType === 'Gate Keeper'"
        class="details-line"
      >
        <template v-if="internalCallType === 'IVR'">
          <span class="details-label">IVR:</span>
          <span class="details-value" :class="{ 'details-empty': !internalIvrNumber }">{{
            internalIvrNumber ? "Press " + internalIvrNumber : "-"
          }}</span>
          <button
            class="icon-action"
            type="button"
            :disabled="isUpdating"
            title="Edit"
            @click="openEdit"
          >
            <Icon icon="mdi:pencil-outline" width="14" height="14" />
          </button>
        </template>
        <template v-else>
          <span class="details-label">Gate Keeper:</span>
          <span class="details-value" :class="{ 'details-empty': !internalGateKeeperName }">{{
            internalGateKeeperName || "-"
          }}</span>
          <button
            class="icon-action"
            type="button"
            :disabled="isUpdating"
            title="Edit"
            @click="openEdit"
          >
            <Icon icon="mdi:pencil-outline" width="14" height="14" />
          </button>
        </template>
      </div>
    </transition>

    <Teleport to="body">
      <transition name="dropdown-fade">
        <div
          v-if="isActionsOpen"
          ref="actionsDropdownMenu"
          class="actions-dropdown-menu"
          :style="actionsDropdownStyle"
        >
          <div class="actions-item" @click="emitEdit">
            <Icon icon="mdi:pencil-outline" width="16" height="16" />
            <span>Edit</span>
          </div>
          <div class="actions-item danger" @click="emitDelete">
            <Icon icon="mdi:delete-outline" width="16" height="16" />
            <span>Delete</span>
          </div>
        </div>
      </transition>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { Icon } from "@iconify/vue";
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useEditContactPhoneNumber } from "@/composables/contacts/useEditContactPhoneNumber";
import { useUpsertContactNumberInfo } from "@/composables/contacts/useUpsertContactNumberInfo";
import { useToast } from "@/composables/ui/useToast";

export interface CallInfoRowProps {
  phoneNumber: string;
  callType?: CallType;
  validity?: Validity;
  ivrNumber?: string;
  gateKeeperName?: string;
  callStatus?: CallStatus;
  isCurrentCall?: boolean;
  callSid?: string;
}

export type Validity = "Valid" | "Invalid" | "Unverified";
export type CallType = "Mobile" | "Direct" | "IVR" | "Gate Keeper";
export type CallStatus =
  | "ready"
  | "connecting"
  | "ringing"
  | "in-call"
  | "ended"
  | "error"
  | "reconnecting";

const VALIDITY_OPTIONS: Validity[] = ["Valid", "Invalid", "Unverified"];
const CALL_TYPES: CallType[] = ["Mobile", "Direct", "IVR", "Gate Keeper"];
const CALL_STATUSES_IN_CALL: CallStatus[] = ["connecting", "ringing", "in-call", "reconnecting"];
const IVR_DIGIT_COUNT = 3;
const DEBOUNCE_MS = 500;
const DROPDOWN_POSITION_DELAY_MS = 10;

const props = withDefaults(
  defineProps<{
    phoneNumber: string;
    contactId: string;
    callType?: CallType;
    validity?: Validity;
    ivrNumber?: string;
    gateKeeperName?: string;
    callStatus?: CallStatus;
    isCurrentCall?: boolean;
    callSid?: string | boolean;
  }>(),
  {
    callType: "Mobile",
    validity: "Unverified",
    ivrNumber: "",
    gateKeeperName: "",
    callStatus: "ready",
    isCurrentCall: false,
    callSid: "",
  }
);

const emit = defineEmits<{
  change: [data: CallInfoRowProps];
  "phone-click": [phoneNumber: string];
  "menu-click": [phoneNumber: string];
  "edit-click": [phoneNumber: string, anchorRect: DOMRect | null];
  "delete-click": [phoneNumber: string];
  "data-updated": [];
}>();

const toast = useToast();
const { editContactPhoneNumber, isEditing } = useEditContactPhoneNumber();
const { upsertNumberInfo, isUpserting } = useUpsertContactNumberInfo();

const isUpdating = computed(() => isEditing.value || isUpserting.value);

function getValidityClass(validity: Validity): string {
  if (validity === "Valid") return "valid";
  if (validity === "Invalid") return "invalid";
  return "unverified";
}

function toE164(value: string): string {
  const trimmed = (value ?? "").toString().trim();
  let normalized = trimmed.replace(/[^0-9+]/g, "");
  if (!normalized.startsWith("+") && normalized.startsWith("00")) {
    normalized = "+" + normalized.slice(2);
  }
  return normalized;
}

const internalValidity = ref<Validity>(
  props.validity && VALIDITY_OPTIONS.includes(props.validity) ? props.validity : "Unverified"
);
const internalCallType = ref<CallType>(
  props.callType && CALL_TYPES.includes(props.callType) ? props.callType : "Mobile"
);
const internalIvrNumber = ref(props.ivrNumber);
const internalIvrParts = ref<string[]>(props.ivrNumber ? props.ivrNumber.split("-") : ["", "", ""]);
const internalGateKeeperName = ref(props.gateKeeperName);

const showIVRInput = ref(false);
const showGateKeeperInput = ref(false);

const isValidityOpen = ref(false);
const isCallTypeOpen = ref(false);
const isActionsOpen = ref(false);

const validityDropdown = ref<HTMLElement | null>(null);
const callTypeDropdown = ref<HTMLElement | null>(null);
const validityDropdownMenu = ref<HTMLElement | null>(null);
const callTypeDropdownMenu = ref<HTMLElement | null>(null);
const actionsDropdown = ref<HTMLElement | null>(null);
const actionsDropdownMenu = ref<HTMLElement | null>(null);
const ivrInputRefs = ref<unknown[]>([]);
const gateKeeperInputRef = ref<HTMLInputElement | null>(null);

const validityDropdownStyle = ref<Record<string, string>>({});
const callTypeDropdownStyle = ref<Record<string, string>>({});
const actionsDropdownStyle = ref<Record<string, string>>({});

type DropdownKind = "validity" | "type" | "actions";

function updateDropdownPosition(
  refEl: HTMLElement | null,
  kind: DropdownKind
): Record<string, string> {
  if (!refEl) return {};
  const rect = refEl.getBoundingClientRect();
  const spacing = 4;

  if (kind === "actions") {
    const menuWidth = 160;
    const top = rect.bottom + spacing;
    const left = rect.right - menuWidth;
    return {
      position: "fixed",
      top: `${top}px`,
      left: `${Math.max(8, left)}px`,
      zIndex: "99999",
    };
  }

  const approxWidth = kind === "validity" ? 180 : 160;
  const approxHeight = kind === "validity" ? 150 : 180;
  const spaceBelow = window.innerHeight - rect.bottom;
  const spaceAbove = rect.top;
  const showAbove = spaceBelow < approxHeight + 20 && spaceAbove > spaceBelow;
  const left = Math.min(Math.max(8, rect.left), window.innerWidth - approxWidth - 8);
  const top = showAbove
    ? Math.max(8, rect.top - approxHeight - spacing)
    : Math.min(rect.bottom + spacing, window.innerHeight - approxHeight - 8);
  return {
    position: "fixed",
    top: `${top}px`,
    left: `${left}px`,
    zIndex: "99999",
    minWidth: `${Math.max(rect.width, 120)}px`,
    maxHeight: `${Math.min(approxHeight, window.innerHeight - 16)}px`,
    overflowY: "auto",
  };
}

watch(
  () => props.validity,
  (newVal) => {
    if (newVal && VALIDITY_OPTIONS.includes(newVal)) {
      internalValidity.value = newVal;
    }
  }
);

watch(
  () => props.callType,
  (newVal) => {
    internalCallType.value = newVal && CALL_TYPES.includes(newVal) ? newVal : "Mobile";
    showIVRInput.value = false;
    showGateKeeperInput.value = false;
  }
);

watch(
  () => props.ivrNumber,
  (newVal) => {
    const raw = newVal ?? "";
    internalIvrNumber.value = raw;
    const parts = raw.split("-");
    internalIvrParts.value = [parts[0] ?? "", parts[1] ?? "", parts[2] ?? ""];
    showIVRInput.value = false;
  }
);

watch(
  () => props.gateKeeperName,
  (newVal) => {
    internalGateKeeperName.value = newVal ?? "";
    showGateKeeperInput.value = false;
  }
);

const saveDebounceTimer = ref<ReturnType<typeof setTimeout> | null>(null);

function scheduleEmitChange() {
  if (saveDebounceTimer.value) clearTimeout(saveDebounceTimer.value);
  saveDebounceTimer.value = setTimeout(() => {
    emitChange();
  }, DEBOUNCE_MS);
}

function toggleValidityDropdown() {
  isValidityOpen.value = !isValidityOpen.value;
  if (isValidityOpen.value) {
    isCallTypeOpen.value = false;
    setTimeout(() => {
      validityDropdownStyle.value = updateDropdownPosition(validityDropdown.value, "validity");
    }, DROPDOWN_POSITION_DELAY_MS);
  }
}

function toggleCallTypeDropdown() {
  isCallTypeOpen.value = !isCallTypeOpen.value;
  if (isCallTypeOpen.value) {
    isValidityOpen.value = false;
    setTimeout(() => {
      callTypeDropdownStyle.value = updateDropdownPosition(callTypeDropdown.value, "type");
    }, DROPDOWN_POSITION_DELAY_MS);
  }
}

function toggleActionsDropdown() {
  isActionsOpen.value = !isActionsOpen.value;
  if (isActionsOpen.value) {
    isValidityOpen.value = false;
    isCallTypeOpen.value = false;
    setTimeout(() => {
      actionsDropdownStyle.value = updateDropdownPosition(actionsDropdown.value, "actions");
    }, DROPDOWN_POSITION_DELAY_MS);
  }
}

async function patchEditNumber(body: {
  phoneNumberType?: "MOBILE" | "DIRECT";
  routeType?: "DIRECT" | "MOBILE" | "IVR" | "GATEKEEPER";
  ivrExtension?: string;
  gatekeeperName?: string;
  isValid?: boolean | null;
}): Promise<boolean> {
  try {
    await editContactPhoneNumber({
      contactId: props.contactId,
      oldPhoneNumber: toE164(props.phoneNumber),
      phoneNumber: toE164(props.phoneNumber),
      ...body,
    });
    toast?.success("Saved");
    emit("data-updated");
    return true;
  } catch {
    return false;
  }
}

async function doUpsertNumberInfo(body: {
  routeType?: "DIRECT" | "MOBILE" | "IVR" | "GATEKEEPER";
  ivrDigits?: string;
  gatekeeperName?: string;
  isValid?: boolean | null;
}): Promise<void> {
  try {
    await upsertNumberInfo({
      contactId: props.contactId,
      phoneNumber: toE164(props.phoneNumber),
      ...body,
    });
    toast?.success("Saved");
    emit("data-updated");
  } catch {
    toast?.error("Failed to save");
  }
}

function selectValidity(validity: Validity) {
  internalValidity.value = validity;
  isValidityOpen.value = false;
  emitChange();
  const isValidLead = validity === "Valid" ? true : validity === "Invalid" ? false : null;
  if (isValidLead === null) {
    patchEditNumber({ isValid: null }).then((ok) => {
      if (!ok) void doUpsertNumberInfo({ isValid: null });
    });
  } else {
    patchEditNumber({ isValid: isValidLead }).then((ok) => {
      if (!ok) void doUpsertNumberInfo({ isValid: isValidLead });
    });
  }
}

const isInCall = computed(() => CALL_STATUSES_IN_CALL.includes(props.callStatus));

function openEdit() {
  if (internalCallType.value === "IVR") {
    showIVRInput.value = true;
    showGateKeeperInput.value = false;
    nextTick(() => (ivrInputRefs.value?.[0] as HTMLInputElement | undefined)?.focus());
  } else if (internalCallType.value === "Gate Keeper") {
    showGateKeeperInput.value = true;
    showIVRInput.value = false;
    nextTick(() => gateKeeperInputRef.value?.focus());
  }
}

watch(
  () => internalIvrNumber.value,
  () => {
    if (internalCallType.value === "IVR") scheduleEmitChange();
  }
);

watch(
  () => internalGateKeeperName.value,
  () => {
    if (internalCallType.value === "Gate Keeper") scheduleEmitChange();
  }
);

function selectCallType(type: CallType) {
  internalCallType.value = type;
  isCallTypeOpen.value = false;

  if (type === "IVR") {
    showIVRInput.value = true;
    showGateKeeperInput.value = false;
    internalGateKeeperName.value = "";
    nextTick(() => (ivrInputRefs.value?.[0] as HTMLInputElement | undefined)?.focus());
  } else if (type === "Gate Keeper") {
    showGateKeeperInput.value = true;
    showIVRInput.value = false;
    internalIvrNumber.value = "";
    internalIvrParts.value = ["", "", ""];
    nextTick(() => gateKeeperInputRef.value?.focus());
  } else {
    showIVRInput.value = false;
    showGateKeeperInput.value = false;
    internalIvrNumber.value = "";
    internalIvrParts.value = ["", "", ""];
    internalGateKeeperName.value = "";
    if (type === "Mobile" || type === "Direct") {
      const phoneNumberType = type === "Mobile" ? "MOBILE" : "DIRECT";
      patchEditNumber({ phoneNumberType }).then((ok) => {
        if (!ok) void doUpsertNumberInfo({ routeType: phoneNumberType as "MOBILE" | "DIRECT" });
      });
    }
  }

  emitChange();
}

function closeOtherEditors(target: "IVR" | "Gate Keeper") {
  if (target === "IVR") showGateKeeperInput.value = false;
  else showIVRInput.value = false;
}

const onIvrFocus = () => closeOtherEditors("IVR");
const onGateKeeperFocus = () => closeOtherEditors("Gate Keeper");

function emitChange() {
  emit("change", {
    phoneNumber: props.phoneNumber,
    callType: internalCallType.value,
    validity: internalValidity.value,
    ivrNumber: internalIvrNumber.value,
    gateKeeperName: internalGateKeeperName.value,
  });
}

async function updateIVRNumbers(ivrNumbers: string[]) {
  const digits = (ivrNumbers.join("") ?? "").replace(/[^0-9]/g, "");
  if (digits.length !== IVR_DIGIT_COUNT) {
    toast?.error(`IVR must be exactly ${IVR_DIGIT_COUNT} digits`);
    return;
  }
  const ok = await patchEditNumber({ routeType: "IVR", ivrExtension: digits });
  if (!ok) await doUpsertNumberInfo({ routeType: "IVR", ivrDigits: digits });
}

async function updateGateKeeperName(gateKeeperName: string) {
  const ok = await patchEditNumber({ routeType: "GATEKEEPER", gatekeeperName: gateKeeperName });
  if (!ok) await doUpsertNumberInfo({ routeType: "GATEKEEPER", gatekeeperName: gateKeeperName });
}

function saveIVR() {
  const parts = internalIvrParts.value.map((p) => (p ?? "").replace(/[^0-9*#+]/g, ""));
  internalIvrParts.value = [parts[0] ?? "", parts[1] ?? "", parts[2] ?? ""];
  const joined = internalIvrParts.value.filter((p) => p?.length).join("-");
  internalIvrNumber.value = joined;
  emitChange();
  showIVRInput.value = false;
  const ivrNumbers = internalIvrParts.value.filter((p) => p?.length);
  updateIVRNumbers(ivrNumbers);
}

function deleteIVR() {
  internalIvrNumber.value = "";
  internalIvrParts.value = ["", "", ""];
  emitChange();
  showIVRInput.value = false;
}

function saveGateKeeper() {
  internalGateKeeperName.value = (internalGateKeeperName.value ?? "").trim();
  emitChange();
  showGateKeeperInput.value = false;
  updateGateKeeperName(internalGateKeeperName.value);
}

function deleteGateKeeper() {
  internalGateKeeperName.value = "";
  emitChange();
  showGateKeeperInput.value = false;
}

function handlePhoneClick() {
  emit("phone-click", props.phoneNumber);
}

function onMenuButtonClick() {
  toggleActionsDropdown();
  emit("menu-click", props.phoneNumber);
}

function emitEdit() {
  const rect = actionsDropdown.value?.getBoundingClientRect() ?? null;
  isActionsOpen.value = false;
  emit("edit-click", props.phoneNumber, rect);
}

function emitDelete() {
  isActionsOpen.value = false;
  emit("delete-click", props.phoneNumber);
}

function handleClickOutside(event: MouseEvent) {
  const target = event.target as Node;
  if (
    validityDropdown.value &&
    !validityDropdown.value.contains(target) &&
    (!validityDropdownMenu.value || !validityDropdownMenu.value.contains(target))
  ) {
    isValidityOpen.value = false;
  }
  if (
    callTypeDropdown.value &&
    !callTypeDropdown.value.contains(target) &&
    (!callTypeDropdownMenu.value || !callTypeDropdownMenu.value.contains(target))
  ) {
    isCallTypeOpen.value = false;
  }
  if (
    actionsDropdown.value &&
    !actionsDropdown.value.contains(target) &&
    (!actionsDropdownMenu.value || !actionsDropdownMenu.value.contains(target))
  ) {
    isActionsOpen.value = false;
  }
}

onMounted(() => {
  document.addEventListener("mousedown", handleClickOutside);
});

onBeforeUnmount(() => {
  document.removeEventListener("mousedown", handleClickOutside);
  if (saveDebounceTimer.value) clearTimeout(saveDebounceTimer.value);
});
</script>

<style scoped>
.call-info-row {
  margin-bottom: 8px;
}

.call-info-main {
  position: relative;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 10px;
  background: #ffffff;
  border: 1px solid #e8e8e8;
  border-radius: 10px;
  transition: all 0.2s ease;
}

.call-info-main:hover {
  border-color: #d0d0d0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.call-info-loading-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  background: rgba(255, 255, 255, 0.85);
  border-radius: 10px;
  z-index: 2;
  pointer-events: none;
}

.call-info-loading-overlay .loading-spinner {
  color: #3e3efe;
  flex-shrink: 0;
}

.call-info-loading-overlay .loading-label {
  font-size: 10px;
  font-weight: 600;
  color: #6b7280;
}

.call-info-row.is-loading .call-info-main {
  pointer-events: none;
}

.validity-selector.is-disabled,
.call-type-selector.is-disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.validity-dropdown {
  position: relative;
}

.validity-selector {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 12px;
  border: 1px solid #e1e1e1;
  background: #ffffff;
  flex-shrink: 0;
  min-width: 36px;
}

.validity-selector:hover {
  border-color: #3e3efe;
  box-shadow: 0 0 0 3px rgba(62, 62, 254, 0.1);
}

.validity-selector.is-open {
  border-color: #3e3efe;
  box-shadow: 0 0 0 3px rgba(62, 62, 254, 0.1);
}

.validity-icon {
  font-size: 13px;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
}

.validity-icon.valid {
  background-color: #16a34a;
  color: #ffffff;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.validity-icon.invalid {
  background-color: #dc2626;
  color: #ffffff;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.validity-icon.unverified {
  background-color: #f59e0b;
  color: #ffffff;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.validity-dropdown-icon {
  color: #6b7280;
  transition: transform 0.2s ease;
  flex-shrink: 0;
}

.validity-dropdown-icon.rotated {
  transform: rotate(180deg);
}

.validity-dropdown-menu {
  background: #ffffff;
  border: 1px solid #e1e1e1;
  border-radius: 8px;
  box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  min-width: 180px;
}

.validity-option {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
  cursor: pointer;
  transition: background-color 0.15s ease;
  border-bottom: 1px solid #f5f5f5;
}

.validity-option:last-child {
  border-bottom: none;
}

.validity-option:hover {
  background-color: #f8f7ff;
}

.validity-option.selected {
  background-color: #f3f1ff;
}

.validity-label {
  font-size: 12px;
  color: #2c2c2e;
  font-weight: 500;
}

.phone-number {
  flex: 1 1 auto;
  font-size: 10px;
  font-weight: 500;
  color: #2c2c2e;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.call-type-dropdown {
  position: relative;
}

.call-type-selector {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  background: #f3f1ff;
  border: 1px solid #e1e1e1;
  border-radius: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 74px;
  max-width: 240px;
  justify-content: space-between;
  flex: 0 1 240px;
}

.call-type-selector:hover {
  border-color: #3e3efe;
  background: #ececff;
}

.call-type-selector.is-open {
  border-color: #3e3efe;
  box-shadow: 0 0 0 3px rgba(62, 62, 254, 0.1);
}

.call-type-label {
  font-size: 10px;
  font-weight: 600;
  color: #3e3efe;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 6px;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.details-line {
  margin-top: 6px;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 10px;
  color: #4b5563;
}

.details-label {
  font-weight: 600;
  color: #6b7280;
}

.details-value {
  font-weight: 600;
  color: #111827;
}

.details-empty {
  color: #9ca3af;
  letter-spacing: 2px;
}

.dropdown-icon {
  color: #3e3efe;
  transition: transform 0.2s ease;
}

.dropdown-icon.rotated {
  transform: rotate(180deg);
}

.call-type-dropdown-menu {
  background: #ffffff;
  border: 1px solid #e1e1e1;
  border-radius: 8px;
  box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  min-width: 160px;
}

.actions-dropdown-menu {
  position: relative;
  background: #ffffff;
  border: 1px solid #e4e4e7;
  border-radius: 10px;
  box-shadow: 0px 8px 24px rgba(0, 0, 0, 0.12);
  overflow: visible;
  min-width: 160px;
  padding: 3px;
}

.actions-dropdown-menu::before {
  content: "";
  position: absolute;
  top: -6px;
  right: 14px;
  width: 10px;
  height: 10px;
  background: #ffffff;
  border-left: 1px solid #e4e4e7;
  border-top: 1px solid #e4e4e7;
  transform: rotate(45deg);
}

.actions-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 14px;
  font-size: 0.8125rem;
  font-weight: 500;
  color: #2c2c2e;
  cursor: pointer;
  border-radius: 6px;
  transition: background 0.15s ease;
  white-space: nowrap;
}

.actions-item:hover {
  background: #f8f7ff;
}

.actions-item.danger {
  color: #dc2626;
}

.actions-item.danger:hover {
  background: #fef2f2;
}

.call-type-option {
  padding: 10px 12px;
  font-size: 12px;
  color: #2c2c2e;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.15s ease;
  border-bottom: 1px solid #f5f5f5;
}

.call-type-option:last-child {
  border-bottom: none;
}

.call-type-option:hover {
  background-color: #f8f7ff;
}

.call-type-option.selected {
  background-color: #f3f1ff;
  color: #3e3efe;
}

.action-icons {
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 0 0 auto;
}

.icon-btn {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.2s ease;
  background: #f8f7ff;
  flex: 0 0 auto;
}

.icon-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.icon-btn:hover {
  transform: scale(1.1);
}

.phone-btn {
  background: #f1eafe;
  color: #7c3aed;
  border: 1px solid rgba(124, 58, 237, 0.18);
  box-shadow: 0 2px 8px rgba(124, 58, 237, 0.18);
}

.phone-btn:hover {
  background: #ede7ff;
  color: #6d28d9;
  box-shadow: 0 3px 10px rgba(124, 58, 237, 0.22);
}

.phone-btn.active-call-btn {
  background: #ff0f0f !important;
  color: #ffffff !important;
  border: 1px solid #ff0f0f !important;
  box-shadow: 0 2px 8px rgba(255, 15, 15, 0.3) !important;
}

.phone-btn.active-call-btn:hover {
  background: #e00e0e !important;
  color: #ffffff !important;
  box-shadow: 0 3px 10px rgba(255, 15, 15, 0.4) !important;
}

.menu-btn {
  background: #3e3efe;
  color: #ffffff;
  border: 1px solid rgba(62, 62, 254, 0.22);
  box-shadow: 0 2px 8px rgba(62, 62, 254, 0.22);
}

.menu-btn:hover {
  background: #2929e4;
  color: #ffffff;
  box-shadow: 0 3px 10px rgba(62, 62, 254, 0.28);
}

.icon-btn .iconify {
  font-size: 14px;
}

.detail-popover {
  position: absolute;
  bottom: calc(100% + 10px);
  left: 50%;
  transform: translateX(-50%);
  min-width: 200px;
  padding: 10px 12px;
  background: #ffffff;
  border: 1px solid #e4e4e7;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  z-index: 9999;
}

.detail-popover::after {
  content: "";
  position: absolute;
  bottom: -7px;
  left: 50%;
  transform: translateX(-50%) rotate(45deg);
  width: 12px;
  height: 12px;
  background: #ffffff;
  border-right: 1px solid #e4e4e7;
  border-bottom: 1px solid #e4e4e7;
}

.detail-popover-close {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  border-radius: 4px;
  background: #f3f4f6;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.15s ease;
}

.detail-popover-close:hover {
  background: #e5e7eb;
  color: #374151;
}

.ivr-group,
.gk-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.ivr-field {
  width: 36px;
  max-width: 36px;
  height: 30px !important;
  padding: 1px 4px !important;
  font-size: 10px;
  text-align: center;
}

.gk-field {
  height: 30px !important;
  padding: 1px 6px !important;
  font-size: 10px;
}

.icon-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 1px solid #e1e1e1;
  background: #ffffff;
  cursor: pointer;
}

.icon-action.save {
  color: #16a34a;
}

.icon-action.save:disabled,
.icon-action.delete:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.icon-action.delete {
  color: #dc2626;
}

.input-label {
  display: block;
  font-size: 10px;
  font-weight: 600;
  color: #646464;
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.input-field {
  width: 100%;
  padding: 6px 10px;
  font-size: 12px;
  color: #2c2c2e;
  background: #ffffff;
  border: 1px solid #e1e1e1;
  border-radius: 6px;
  transition: all 0.2s ease;
  font-family: inherit;
}

.input-field:focus {
  outline: none;
  border-color: #3e3efe;
  box-shadow: 0 0 0 3px rgba(62, 62, 254, 0.1);
}

.input-field::placeholder {
  color: #a0a0a0;
}

.dropdown-fade-enter-active,
.dropdown-fade-leave-active {
  transition: all 0.2s ease;
}

.dropdown-fade-enter-from {
  opacity: 0;
  transform: translateY(-8px);
}

.dropdown-fade-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

.slide-fade-enter-active {
  transition: all 0.3s ease;
}

.slide-fade-leave-active {
  transition: all 0.2s ease;
}

.slide-fade-enter-from {
  opacity: 0;
  transform: translateY(-10px);
  max-height: 0;
}

.slide-fade-enter-to {
  opacity: 1;
  transform: translateY(0);
  max-height: 100px;
}

.slide-fade-leave-from {
  opacity: 1;
  transform: translateY(0);
  max-height: 100px;
}

.slide-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
  max-height: 0;
}
</style>
